'use client';

import type { VariantProps } from 'class-variance-authority';
import { buttonVariants } from '../ui/button';
import { Button as UIButton } from '../ui/button';
import { IconButton as UIIconButton, buttonVariants as iconButtonVariants } from '../ui/icon-button';
import type { MouseEventHandler } from 'react';
import { Loader } from '../Loader';
import { cn } from '../../lib/utils';

export type TButtonProps = VariantProps<typeof buttonVariants> & {
  loading?: boolean;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
  children: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  id?: string;
  title?: string;
  'aria-label'?: string;
};

export type TIconButtonProps = Omit<TButtonProps, 'variant' | 'size'> &
  VariantProps<typeof iconButtonVariants> & {
    className?: string;
  };

export function Button({
  loading,
  disabled,
  onClick,
  children,
  type = 'button',
  variant,
  className,
  size = 'default',
  ...props
}: TButtonProps) {
  return (
    <UIButton
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
      variant={variant as typeof variant | undefined}
      size={size}
      className={cn(loading && 'cursor-not-allowed', className)}
      {...props}>
      {loading && <Loader className="mr-2 h-5 w-5 text-button-primary-disabled-text" />}
      {loading ? <span>Aguarde...</span> : children}
    </UIButton>
  );
}

export function IconButton({
  loading,
  disabled,
  onClick,
  children,
  type = 'button',
  variant = 'ghost',
  size = 'icon',
  className,
  ...props
}: TIconButtonProps) {
  return (
    <UIIconButton
      variant={variant}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-full p-0',
        size === 'sm' && 'h-6 w-6',
        size === 'lg' && 'h-10 w-10',
        className,
        (disabled || loading) && 'cursor-not-allowed',
      )}
      onClick={onClick}
      type={type}
      disabled={loading || disabled}
      {...props}>
      {loading ? <Loader className="h-5 w-5 text-white" /> : children}
    </UIIconButton>
  );
}
